import { GiftType } from '.';
import * as Gifts from '.';
import { action, asyncAction } from '@play-co/replicant';
import createActions from '../config/actions/createActions';
import {
  addGiftGems,
  cleanupReceivedGifts,
  cleanupSentGifts,
  removeGift,
} from './gifts.modifiers';
import { Feature, sendAnalyticsEvents } from '../analytics';
import { getChatbotFlexMessage } from '../chatbot/flex/utils';
import { deliverChatbotMessage } from '../chatbot/chatbot.util';
import { getLocale } from '../getters/user';
import { getEquippedPetSpecies } from '../pet/pet.getters';
import { getPlayerProfileFromLocale } from '../profile/profile.getters';
import { duration } from '@play-co/components';
import { AlternativeOpenWebBuckets } from '../utils/payload';
import { createReportableError } from '../utils/errors';

import * as Inventory from '../inventory';

export default createActions({
  /**
   * Send gifts to target user over replicant message
   * It won't send U2U message! It will send only reward message
   *
   * Each target becomes a separate entry fetch, message write and index rebuild on
   * the server, fanned out under one unbounded Promise.all, so callers must batch
   * at `giftBatchSize` rather than pass a whole friend list (#11690).
   *
   * Returns the targets actually written, which is not the same as the input —
   * anything failing `canSendGift` server-side is skipped. Callers use the return
   * value to decide who to notify.
   */
  sendGiftsMessages: action((state, targetUserIds: string[], api) => {
    const now = api.date.now();
    const sentIds: string[] = [];

    // Targets that are no longer sendable, typically a repeat send on the same JST
    // day. Counted and reported once rather than per target: one report per
    // recipient fans out a Sentry capture per id, which is itself a load source on
    // a large friend list.
    let skippedCount = 0;

    for (const targetUserId of targetUserIds) {
      // Check if we can send to user
      if (!Gifts.getters.canSendGift(state, targetUserId, now)) {
        skippedCount += 1;
        continue;
      }

      // Create record about sent reward
      state.gifts.sent[targetUserId] = { sentAt: now };

      // Send gift message to target player
      api.postMessage.receiveGift(targetUserId, {});

      sentIds.push(targetUserId);
    }

    if (skippedCount) {
      api.reportError(
        createReportableError(
          'warning',
          new Error(
            `Can't send gift to ${skippedCount} of ${targetUserIds.length} users`,
          ),
        ),
      );
    }

    // Self cleanup
    // We want to avoid crease user state
    // For this we will clean up old sent messages on every new message send
    cleanupSentGifts(state, now);

    return sentIds;
  }),

  /**
   * Consume one gift from selected sender
   */
  consumeGift: action((state, senderId: string, api) => {
    // Sanitize input
    if (!senderId) throw new Error('Wrong senderId');

    const gift = state.gifts.received[senderId];
    const energyToAdd = gift?.energy;
    const pointsToAdd = gift?.bPoints;
    const gemsToAdd = gift?.gems;
    if (!energyToAdd && !pointsToAdd && !gemsToAdd)
      throw new Error('Gift not found');

    const now = api.date.now();

    let giftType: GiftType = 'bPoint';
    if (energyToAdd) giftType = 'energy';
    if (gemsToAdd) giftType = 'gems';

    const analytics = {
      gift: giftType,
      amount: energyToAdd ?? pointsToAdd ?? gemsToAdd ?? 0,
    } as const;

    const feature = {
      feature: Feature.GIFTS._,
      $subFeature: `${Feature.GIFTS._}_${analytics.gift}_single`,
    };

    // How many energy we got and how many energy we have now
    if (energyToAdd) {
      // Check if we won't reach energy gifts limit
      if (!Gifts.getters.canCollect(state, senderId, now))
        throw new Error(
          'You can’t collect free energy when your energy balance is over ' +
            Gifts.ruleset.maxClaimEnergy,
        );

      // All good give energy from gift
      Inventory.modifiers.addEnergy(state, energyToAdd, now, feature, api);
    }

    // Add any points sent in the gift
    if (pointsToAdd) {
      Inventory.modifiers.addPoints(state, pointsToAdd, feature, api);
    }

    // Add any gems sent in the gift.
    // The daily cap is enforced on the client only (#12235): a server-side
    // reject would fail for old clients during rollout. Add the server check in
    // a follow-up release once old clients are gone.
    if (gemsToAdd) {
      addGiftGems(state, gemsToAdd, now, feature, api);
    }

    // Remove gift record from state
    removeGift(state, senderId);

    // Self cleanup
    // We want to avoid increasing user state
    // For this we will clean up old received messages when consuming gifts
    cleanupReceivedGifts(state, now);

    // Gift energy the player sent has been collected by the LINE friend
    // This event is sent as the sender’s inactive event
    sendAnalyticsEvents(api, [
      {
        userId: senderId,
        eventType: 'GiftCollected',
        eventProperties: {
          ...analytics,
          receiver: state.id, // Receiver should be current user for this event
        },
      },
    ]);

    return {
      ...analytics,
      sender: senderId,
    };
  }),

  /**
   * Consume gifts received from the given senders
   *
   * Each collected gift emits a `GiftCollected` event addressed to its sender, and
   * the server resolves receiver context for every distinct addressee. Cheaper per
   * item than `sendGiftsMessages`, but it rides the same request, so callers batch
   * it alongside them at `giftBatchSize` (#11690).
   *
   * Senders with no pending gift are ignored, which keeps a retried batch a no-op.
   *
   * `isLastBatch` gates the stale-gift cleanup, which prunes the whole received
   * map rather than just this batch. Running it mid-run would delete gifts that a
   * later batch is still queued to collect.
   */
  consumeAllGifts: action(
    (state, args: { senderIds: string[]; isLastBatch?: boolean }, api) => {
      const { senderIds } = args;
      const analytics: {
        gift: GiftType;
        amount: number;
        sender: string;
      }[] = [];
      const now = api.date.now();
      const feature = {
        feature: Feature.GIFTS._,
        $subFeature: Feature.GIFTS.SEND_ENERGY_ALL,
      };

      // Move over all senders and add energy/bPoints
      for (let index = 0; index < senderIds.length; index++) {
        const senderId = senderIds[index];
        const gift = state.gifts.received[senderId];
        const energyToAdd = gift?.energy;
        const pointsToAdd = gift?.bPoints;
        const gemsToAdd = gift?.gems;

        // If both property are 0, this mean gift already collected
        if (!energyToAdd && !pointsToAdd && !gemsToAdd) continue;

        if (energyToAdd) {
          // Skip energy that we can't consume
          if (!Gifts.getters.canCollect(state, senderId, now)) continue;

          // All good give energy from gift
          Inventory.modifiers.addEnergy(state, energyToAdd, now, feature, api);
        }

        if (pointsToAdd) {
          Inventory.modifiers.addPoints(state, pointsToAdd, feature, api);
        }

        // Add any gems sent in the gift. Not capped here, see consumeGift.
        if (gemsToAdd) {
          addGiftGems(state, gemsToAdd, now, feature, api);
        }

        // Remove gift record from state
        removeGift(state, senderId);

        let giftType: GiftType = 'bPoint';
        if (energyToAdd) giftType = 'energy';
        if (gemsToAdd) giftType = 'gems';

        // Collect analytics data for each successfully collected gift
        analytics.push({
          gift: giftType,
          amount: energyToAdd ?? pointsToAdd ?? gemsToAdd ?? 0,
          sender: senderId,
        });
      } // for

      // Self cleanup
      // We want to avoid increasing user state
      // For this we will clean up old received messages when consuming gifts.
      // Deferred to the last batch: this prunes the whole map, so running it now
      // would delete gifts a later batch has not collected yet.
      if (args.isLastBatch) cleanupReceivedGifts(state, now);

      // No gifts
      if (!analytics.length) return analytics;

      // Collect analytics for sending it as batch
      const events = analytics.map(
        ({ gift, amount, sender }) =>
          ({
            userId: sender,
            eventType: 'GiftCollected',
            eventProperties: {
              gift,
              amount,
              receiver: state.id, // Receiver should be current user for this event
            },
          }) as const,
      );

      // Gift energy the player sent has been collected by the LINE friend
      // This event is sent as the sender’s inactive event
      sendAnalyticsEvents(api, [...events]);

      // Return analytics for client
      return analytics;
    },
  ),

  /**
   * Send OA message gift
   */
  asyncSendGiftsOaMessage: asyncAction(
    async (state, args: { receiverIds: string[]; subFeature: string }, api) => {
      const receivers = await api.asyncGetters.getSendGiftsOaMessageDetails({
        receiverIds: args.receiverIds,
        subFeature: args.subFeature,
      });
      const { photo, name } = getPlayerProfileFromLocale(
        getLocale(state),
        state.publicProfile?.name,
        state.publicProfile?.photo,
        state.profile?.name,
        state.profile?.photo,
      );
      const species = getEquippedPetSpecies(state) ?? 'ball';

      // Send message
      for (const receiver of receivers) {
        // Create message
        const message = getChatbotFlexMessage({
          template: 'giftMessage',
          payload: receiver.payload,
          data: {
            sender: { photo, name, species },
            locale: receiver.locale,
          },
          analyticsUserProperties: {},
        });

        // Otherwise send message immediately.
        deliverChatbotMessage(state, api, {
          receiverId: receiver.id,
          message,
          channel: 'oa', // Force OA channel
        });
      }
    },
  ),
});
